import { useState } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, ArrowUp, ArrowDown, Trash2, Youtube, Undo2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, asset, formatApiError } from "@/lib/api";
import { uploadFile } from "../FileField";
import { SourceChip } from "./trackBits";

const mini = "w-full bg-transparent border-b border-transparent hover:border-[#F5A524]/30 focus:border-[#F5A524] outline-none py-0.5";
const Icon = ({ testid, label, onClick, danger, disabled, children }) => (
  <button type="button" data-testid={testid} aria-label={label} title={label} onClick={onClick} disabled={disabled}
    className={`w-8 h-8 grid place-items-center border transition-colors disabled:opacity-30 ${danger ? "border-[#FF2A55]/40 text-[#FF2A55] hover:bg-[#FF2A55] hover:text-black" : "border-[#F5A524]/30 text-zinc-300 hover:text-[#F5A524]"}`}>
    {children}
  </button>
);

export async function findFull(t) {
  const { data } = await api.get("/music/match", { params: { title: t.title, artist: t.artist } });
  return { video_id: data.video_id, match_title: data.match_title };
}

const FullVersion = ({ t, i, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (t.source !== "spotify") return null;
  const find = async () => {
    setBusy(true);
    try { onChange(await findFull(t)); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  if (t.video_id) {
    return (
      <span className="flex items-center gap-2 min-w-0 text-[11px] text-zinc-400">
        <span data-testid={`music-queue-full-${i}`} className="truncate"><span className="text-[#F5A524] font-mono tracking-[0.15em]">FULL VIA YOUTUBE</span> · {t.match_title}</span>
        <button type="button" data-testid={`music-queue-unmatch-${i}`} onClick={() => onChange({ video_id: "", match_title: "" })} className="shrink-0 flex items-center gap-1 text-zinc-300 hover:text-[#FF2A55]"><Undo2 size={11} /> PREVIEW</button>
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2 text-[11px]">
      <span data-testid={`music-queue-preview-${i}`} className="font-mono tracking-[0.15em] text-zinc-400">30S PREVIEW</span>
      <button type="button" data-testid={`music-queue-match-${i}`} onClick={find} disabled={busy} className="flex items-center gap-1 text-[#F5A524] hover:text-white disabled:opacity-50">
        <Youtube size={12} /> {busy ? "SEARCHING..." : "PLAY FULL VERSION"}
      </button>
    </span>
  );
};

const Cover = ({ t, i, onChange }) => {
  const [busy, setBusy] = useState(false);
  const img = <img src={asset(t.cover)} alt="" className="w-12 h-12 object-cover bg-black" />;
  if (t.source !== "file") return <span className="shrink-0">{img}</span>;
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try { onChange({ cover: (await uploadFile(f, () => {})).url }); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <label data-testid={`music-queue-cover-${i}`} title="Upload cover art" className="relative shrink-0 w-12 h-12 cursor-pointer group border border-dashed border-[#00F0FF]/40 grid place-items-center">
      {t.cover ? img : <ImagePlus size={16} className="text-[#00F0FF]" />}
      {busy && <span className="absolute inset-0 grid place-items-center bg-black/70 font-mono text-[9px]">...</span>}
      <input type="file" accept="image/*" className="hidden" onChange={onFile} />
    </label>
  );
};

export const QueueRow = ({ t, i, total, onChange, onMove, onRemove }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item value={t} dragListener={false} dragControls={controls} data-testid={`music-queue-row-${i}`}
      className="flex items-center gap-3 bg-[#0B0C10] border border-[#F5A524]/15 p-2 select-none" whileDrag={{ scale: 1.02, boxShadow: "0 0 0 1px #F5A524" }}>
      <button type="button" data-testid={`music-queue-drag-${i}`} aria-label="Drag to reorder" onPointerDown={(e) => controls.start(e)}
        className="shrink-0 cursor-grab active:cursor-grabbing text-zinc-400 hover:text-[#F5A524] touch-none"><GripVertical size={18} /></button>
      <span className="hidden sm:block shrink-0 w-6 font-mono text-[11px] text-[#F5A524]">{String(i + 1).padStart(2, "0")}</span>
      <Cover t={t} i={i} onChange={onChange} />
      <div className="min-w-0 flex-1 space-y-0.5">
        <input data-testid={`music-queue-title-${i}`} value={t.title} onChange={(e) => onChange({ title: e.target.value })} placeholder="Title" className={`${mini} text-sm text-white`} />
        <div className="flex items-center gap-2">
          <SourceChip t={t} />
          <input data-testid={`music-queue-artist-${i}`} value={t.artist} onChange={(e) => onChange({ artist: e.target.value })} placeholder="Artist" className={`${mini} text-xs text-zinc-400`} />
        </div>
        <FullVersion t={t} i={i} onChange={onChange} />
      </div>
      <div className="shrink-0 flex flex-col sm:flex-row gap-1">
        <Icon testid={`music-queue-up-${i}`} label="Move up" onClick={() => onMove(-1)} disabled={i === 0}><ArrowUp size={13} /></Icon>
        <Icon testid={`music-queue-down-${i}`} label="Move down" onClick={() => onMove(1)} disabled={i === total - 1}><ArrowDown size={13} /></Icon>
        <Icon testid={`music-queue-remove-${i}`} label="Remove from queue" onClick={onRemove} danger><Trash2 size={13} /></Icon>
      </div>
    </Reorder.Item>
  );
};
